import DataTableColumnHeader from "@/components/Common/DataTable/DataTableColumnHeader";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Task } from "@/types/task";
import { TaskStatus } from "@/consts/Task";
import { ColumnDef } from "@tanstack/react-table";
import TaskSelect from "./components/TaskSelect";
import { ReactComponent as MessageSvg } from "@/assets/images/tabler-message-filled.svg";
import CommonImg from "@/components/Common/Img";
import CalendarSvg from "@/assets/images/calendar.svg";
import { Plus } from "lucide-react";
import { calculateDDay, cn, formatDate } from "@/libs/utils";
const statusOrder = [
  TaskStatus.OPENED,
  TaskStatus.IN_PROGRESS,
  TaskStatus.REVIEW,
  TaskStatus.CLOSED,
  TaskStatus.CANCELED,
] as const;

const statusSortingFn = (rowA: any, rowB: any, columnId: any) => {
  const statusA = rowA.getValue(columnId);
  const statusB = rowB.getValue(columnId);

  return (
    statusOrder.indexOf(statusA as TaskStatus) -
    statusOrder.indexOf(statusB as TaskStatus)
  );
};

export const createColumns = (
  navigate: ReturnType<typeof import("react-router-dom").useNavigate>,
  groupId: string
): ColumnDef<Task>[] => {
  return [
    {
      accessorKey: "task_title",
      header: ({ column }) => (
        <DataTableColumnHeader title="작업명" column={column} />
      ),
      cell: ({ row }) => {
        const { task_title } = row.original;
        return <p className="text-sm text-gray-A font-normal">{task_title}</p>;
      },
      enableSorting: false,
      size: 425,
    },
    {
      accessorKey: "assignor",
      header: ({ column }) => (
        <DataTableColumnHeader title="요청자" column={column} />
      ),
      cell: ({ row }) => {
        const { assignor } = row.original;
        return (
          <div className="flex items-center gap-2">
            <Avatar className="size-6">
              <AvatarImage src={assignor.img_url} />
              <AvatarFallback>R</AvatarFallback>
            </Avatar>
            <span className="whitespace-nowrap text-xs text-gray-A font-normal">
              {assignor.user_name}
            </span>
          </div>
        );
      },
      size: 120,
    },
    {
      accessorKey: "assignees",
      header: ({ column }) => (
        <DataTableColumnHeader title="작업자" column={column} />
      ),
      cell: ({ row }) => {
        const { assignees } = row.original;
        return (
          <>
            {assignees.length === 1 ? (
              <div className="flex items-center gap-2">
                <Avatar className="size-6">
                  <AvatarImage src={assignees[0].img_url} />
                  <AvatarFallback>R</AvatarFallback>
                </Avatar>
                <span className="whitespace-nowrap text-xs text-gray-A font-normal">
                  {assignees[0].user_name}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-4">
                  {assignees.slice(0, 2).map((assignees, index) => (
                    <div
                      className="flex items-center gap-2"
                      key={`assignees-${assignees.user_id}-${index}`}
                    >
                      <Avatar className="size-6">
                        <AvatarImage src={assignees.img_url} />
                        <AvatarFallback>{assignees.user_name}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-gray-A font-normal">
                        {assignees.user_name}
                      </span>
                    </div>
                  ))}
                </div>
                {assignees.length > 2 && (
                  <div className="flex items-center text-gray-B">
                    <Plus className="size-3.5" />
                    <span className="text-sm font-semibold">
                      {assignees.length - 2}
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        );
      },
      size: 225,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader title="진행상태" column={column} />
      ),
      cell: ({ row }) => {
        const status: TaskStatus = row.getValue("status");
        const taskId: string = row.original.task_id;

        return <TaskSelect status={status} taskId={taskId} />;
      },
      size: 100,
      sortingFn: statusSortingFn,
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader title="요청일" column={column} />
      ),
      cell: ({ row }) => (
        <div className="flex items-center text-xs text-gray-A font-semibold ">
          {formatDate(row.getValue("created_at"))}
        </div>
      ),
      size: 150,
    },
    {
      accessorKey: "due_date",
      header: ({ column }) => (
        <DataTableColumnHeader title="마감일" column={column} />
      ),
      cell: ({ row }) => {
        const dueDate = row.getValue("due_date") as number;
        const dDay = calculateDDay(dueDate);

        return (
          <div className="flex items-center gap-1">
            <CommonImg
              src={CalendarSvg}
              alt="calendar"
              className="text-gray-A"
            />
            <span
              className={cn("text-xs", {
                "text-red font-bold": dDay <= 3 && dDay >= 0, // D-3일 이하일 때 빨간색 강조
                "text-gray-A font-normal": dDay > 3 || dDay < 0, // 그 외에는 기본 스타일
              })}
            >
              {formatDate(dueDate)}
            </span>
          </div>
        );
      },
      size: 150,
    },
    {
      accessorKey: "memos",
      header: ({ column }) => (
        <DataTableColumnHeader title="유저메시지" column={column} />
      ),
      cell: ({ row }) => {
        const memo = row.original.latest_user_memo || "";

        const handleNavigateToTaskDetail = () => {
          navigate(
            `/management/projects/${groupId}/tasks/${row.original.task_id}`
          );
        };

        return (
          <div className="flex items-center justify-between gap-2">
            <span className="truncate overflow-hidden whitespace-nowrap font-normal">
              {memo}
            </span>
            <span
              className="p-1 bg-dark-red rounded-sm cursor-pointer"
              onClick={handleNavigateToTaskDetail}
            >
              <MessageSvg />
            </span>
          </div>
        );
      },
      enableSorting: false,
      size: 265,
    },
  ];
};
